import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { 
  Table, Button, Space, Tag, Breadcrumb, message, Popconfirm, Spin, Alert,
  Drawer, Form, Input, Card, Switch, Modal, Radio, Upload, Select
} from "antd";
import { 
  ExportOutlined, CaretUpFilled, CaretRightFilled, 
  DeleteOutlined, FileTextOutlined, PlusOutlined,
  ArrowUpOutlined, ArrowDownOutlined, EditOutlined,
  DesktopOutlined, SettingOutlined, UploadOutlined, FilePdfOutlined,
  PictureOutlined, LockOutlined, UnlockOutlined
} from "@ant-design/icons";
import { TbHandClick } from "react-icons/tb";
import CustomHeader from "../container/Header";
import { useGlobal } from "../global/user";
import { Machine, Field } from "../api";
import { buildLocator } from "../utils/stepLocator";
import "antd/dist/reset.css";

import { 
  GetInstructions, 
  CreateInstruction, 
  UpdateInstruction, 
  DeleteInstruction,
  DownloadInstructionPDF 
} from "../api/tutorial";

const TUTORIAL_ACTION_PASSWORD = "admin";

const TUTORIAL_FIELD_ID = 44;
const TUTORIAL_MACHINE_PREFIX = "教學範例-";
const TUTORIAL_MACHINE_DEFAULTS = { IP: "1.iotwebhub.net", port: 1, socket_port: 1 };

const MACHINE_TYPES = [
  { value: 1, label: "索道權限" },
  { value: 2, label: "室內定位" },
  { value: 3, label: "影像機具" },
  { value: 4, label: "電源權限" },
  { value: 5, label: "數位油壓" },
  { value: 6, label: "電源控制" },
  { value: 7, label: "儲量監測" },
  { value: 8, label: "水情影像" },
  { value: 9, label: "MQTT油壓" },
  { value: 10, label: "門禁機具" },
  { value: 11, label: "MQTT機具" },
  { value: 12, label: "紅外線鏡頭" },
  { value: 13, label: "鹽霧害影像機具" },
];

const NO_SOCKET_TYPES = [3, 8, 9, 10, 11, 12, 13];

const isImageStep = (step) => !!step && typeof step === "object" && step.type === "IMAGE";

const getStepLabel = (step) => {
  if (step == null) return "";
  if (typeof step === "string") return step;
  if (isImageStep(step)) return step.text || step.file_name || "說明圖片";
  return String(step.text ?? step.label ?? step.target_value ?? "");
};

const renderStepTag = (step, color) => (
  <Tag color={color} icon={isImageStep(step) ? <PictureOutlined /> : null}>
    {getStepLabel(step)}
  </Tag>
);

const PdfViewer = ({ filename }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let currentUrl = null;
    let isMounted = true;

    const fetchPdf = async () => {
      try {
        setLoading(true);
        const blobData = await DownloadInstructionPDF(filename);
        
        const pdfBlob = new Blob([blobData], { type: "application/pdf" });
        currentUrl = URL.createObjectURL(pdfBlob);

        if (isMounted) {
          setBlobUrl(currentUrl);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError("PDF 載入失敗，請稍後重試");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (filename) {
      fetchPdf();
    }

    return () => {
      isMounted = false;
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [filename]);

  if (loading) {
    return (
      <div style={{ padding: "30px 0", textAlign: "center" }}>
        <Spin tip="PDF 檔案載入中..." />
      </div>
    );
  }

  if (error) {
    return <Alert message={error} type="error" showIcon style={{ margin: "12px 0" }} />;
  }

  return (
    <iframe
      src={blobUrl}
      title={`pdf-preview-${filename}`}
      width="100%"
      height="450px"
      style={{ border: "1px solid var(--border-color)", borderRadius: "6px" }}
    />
  );
};


export default function Tutorial() {
  const history = useHistory();
  const [form] = Form.useForm();
  const { user } = useGlobal();
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sections, setSections] = useState([]);
  const [pathSteps, setPathSteps] = useState([]);          
  const [displayPathSteps, setDisplayPathSteps] = useState([]); 
  const [isTracking, setIsTracking] = useState(false);

  const [recordingModalVisible, setRecordingModalVisible] = useState(false);
  const [recordingPath, setRecordingPath] = useState("");
  const iframeRef = useRef(null);

  const [preparingMachine, setPreparingMachine] = useState(false);
  const [recordingMachine, setRecordingMachine] = useState(null); 
  const [switchingField, setSwitchingField] = useState(false);    
  const suppressRecordRef = useRef(false);                         

  const [stepModalVisible, setStepModalVisible] = useState(false);
  const [pendingStep, setPendingStep] = useState(null);            
  const [pendingStepName, setPendingStepName] = useState("");      
  const [tempDisplayStepName, setTempDisplayStepName] = useState("");

  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState("");
  const [tempImageFileName, setTempImageFileName] = useState("");
  const [tempImageCaption, setTempImageCaption] = useState("");

  const [unlocked, setUnlocked] = useState(false);
  const [unlockModalVisible, setUnlockModalVisible] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState(false);

  const closeUnlockModal = () => {
    setUnlockModalVisible(false);
    setUnlockPassword("");
    setUnlockError(false);
  };

  const handleLockClick = () => {
    if (unlocked) {
      setUnlocked(false);
      setDrawerVisible(false);
      resetForm();
      message.info("已重新鎖定");
      return;
    }
    setUnlockModalVisible(true);
  };

  const handleUnlockSubmit = () => {
    if (unlockPassword === TUTORIAL_ACTION_PASSWORD) {
      setUnlocked(true);
      closeUnlockModal();
      message.success("已解鎖，可進行新增、編輯與刪除");
    } else {
      setUnlockError(true);
      message.error("密碼錯誤！");
    }
  };

  const serverBaseUrl = process.env.REACT_APP_BACKEND;
  const uploadApiAction = `${serverBaseUrl}instruction/upload_file`;

  const fetchInstructions = async () => {
    setLoading(true);
    try {
      const res = await GetInstructions();
      const list = Array.isArray(res) 
        ? res 
        : (res?.data || res?.result || []);

      const formattedData = list.map((item) => {
        let formattedDate = "";
        if (item.updated_at) {
          const d = new Date(item.updated_at);
          if (!isNaN(d.getTime())) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            formattedDate = `${year} / ${month} / ${day}`;
          }
        }

        return {
          key: item.id,
          id: item.id,
          title: item.title,
          targetPath: item.target_page_path,
          pathSteps: typeof item.path_steps === "string" ? JSON.parse(item.path_steps) : (item.path_steps || []),
          displayPathSteps: typeof item.display_path_steps === "string" ? JSON.parse(item.display_path_steps) : (item.display_path_steps || []),
          sections: typeof item.sections === "string" ? JSON.parse(item.sections) : (item.sections || []),
          updatedAt: formattedDate,
          restoreData: {
            targetPath: item.target_page_path,
            pathSteps: item.path_steps,
            displayPathSteps: item.display_path_steps,
          },
        };
      });
      setData(formattedData);
    } catch (error) {
      message.error("載入設定說明失敗");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstructions();
  }, []);

  const prepareRecordingMachine = async (machineType) => {
    if (!TUTORIAL_FIELD_ID) {
      message.error("尚未設定教學場域（REACT_APP_TUTORIAL_FIELD_ID）");
      return false;
    }

    const typeInfo = MACHINE_TYPES.find((t) => t.value === machineType);
    const machineName = `${TUTORIAL_MACHINE_PREFIX}${typeInfo?.label || machineType}`;

    setPreparingMachine(true);
    try {
      try {
        const res = await Field.GetFieldData(TUTORIAL_FIELD_ID, user?.class);
        const list = Array.isArray(res?.data) ? res.data : [];
        const existing = list.find((m) => m.mobile_name === machineName);
        if (existing) {
          setRecordingMachine({ id: existing.id, name: machineName });
          message.info(`教學場域已有範例機具「${machineName}」，直接沿用`);
          return true;
        }
      } catch (e) {
        console.error(e);
      }

      const machineInfo = {
        mobileType: machineType,
        name: machineName,
        IP: TUTORIAL_MACHINE_DEFAULTS.IP,
        field_id: TUTORIAL_FIELD_ID,
      };
      if (!NO_SOCKET_TYPES.includes(machineType)) {
        machineInfo.port = TUTORIAL_MACHINE_DEFAULTS.port;
        machineInfo.socket_port = TUTORIAL_MACHINE_DEFAULTS.socket_port;
      }

      const response = await Machine.CreateMachine(machineInfo);
      if (response?.code === 200) {
        setRecordingMachine({ id: response.data?.machine?.id, name: machineName });
        message.success(`已在教學場域新增範例機具「${machineName}」`);
        return true;
      }
      message.error("新增範例機具失敗");
      return false;
    } catch (error) {
      message.error(error?.response?.data?.message || "新增範例機具失敗");
      return false;
    } finally {
      setPreparingMachine(false);
    }
  };

  const handleTrackingChange = async (checked) => {
    if (checked) {
      const targetPath = form.getFieldValue("target_page_path");
      if (!targetPath) {
        message.warning("開啟錄製前，請先選擇目標頁面！");
        return;
      }

      setRecordingMachine(null);
      if (targetPath === "/control") {
        const machineType = form.getFieldValue("machine_type");
        if (!machineType) {
          message.warning("請先選擇機具類型！");
          return;
        }
        const ready = await prepareRecordingMachine(machineType);
        if (!ready) return;
      }

      setPathSteps([]);
      setDisplayPathSteps([]);
      
      const fullUrl = `${window.location.origin}/IoT3/#${targetPath}`;
      setRecordingPath(fullUrl);
      
      setIsTracking(true);
      setRecordingModalVisible(true);
    } else {
      handleStopTracking();
    }
  };

  const getReactKey = (el) => {
    const fiberKey = Object.keys(el).find(
      (k) => k.startsWith("__reactFiber$") || k.startsWith("__reactInternalInstance$")
    );
    return fiberKey ? el[fiberKey]?.key : null;
  };

  const clickInIframe = (el) => {
    suppressRecordRef.current = true;
    try {
      el.click();
    } finally {
      suppressRecordRef.current = false;
    }
  };

  const switchIframeField = async (iframeWindow) => {
    setSwitchingField(true);
    try {
      const doc = iframeWindow.document;

      const waitFor = async (getter, timeout = 10000) => {
        const start = Date.now();
        while (Date.now() - start < timeout) {
          if (!iframeRef.current) return null;
          const result = getter();
          if (result) return result;
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return null;
      };

      const selectText = await waitFor(() => doc.querySelector(".select_group .select_text"));
      if (!selectText) throw new Error("找不到場域選單");

      clickInIframe(selectText);

      const option = await waitFor(() =>
        Array.from(doc.querySelectorAll(".select_group .dropdown_menu li")).find(
          (li) => String(getReactKey(li)) === String(TUTORIAL_FIELD_ID)
        )
      );
      if (!option) throw new Error(`找不到場域 ID ${TUTORIAL_FIELD_ID} 的選項`);
      clickInIframe(option);

      await waitFor(() => !doc.querySelector(".select_group .dropdown_menu"), 3000);
    } catch (err) {
      console.warn("自動切換教學場域失敗：", err);
      message.warning("無法自動切換到教學場域，請在錄製視窗內手動切換");
    } finally {
      setSwitchingField(false);
    }
  };

  const handleIframeLoad = () => {
    try {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return;

      const handleIframeClick = (e) => {
        if (suppressRecordRef.current) return;
        const targetEl = e.target;

        // --- 1. 計算雙重條件的 matchIndex ---
        const target_has_class = e.target.closest('[class]');
        let matchIndex = 0;

        if (target_has_class) {
          const targetClass = target_has_class.classList[0];
          const targetText = target_has_class.innerText ? target_has_class.innerText.trim() : '';

          if (targetClass && iframeWindow.document) {
            const allElements = iframeWindow.document.querySelectorAll(`.${targetClass}`);
            
            const matchedElements = Array.from(allElements).filter(el => {
              const elText = el.innerText ? el.innerText.trim() : '';
              return elText === targetText;
            });

            const index = matchedElements.indexOf(target_has_class);

            if (index !== -1) {
              matchIndex = index;
              console.log(`符合條件：Class 為 [${targetClass}] 且文字為 [${targetText}]`);
              console.log(`第：${index + 1} 個 (索引 ${index})`);
            }
          }
        }
        // ------------------------------------

        const ignoreTags = ["BODY", "HTML", "MAIN", "SECTION", "DIV", "ARTICLE"];
        const isInteractiveElement = targetEl.closest('button, a, [role="button"], [role="tab"], .ant-tabs-tab, input, select');

        if (ignoreTags.includes(targetEl.tagName) && !isInteractiveElement) {
          return;
        }

        const { label, text, locator } = buildLocator(targetEl);

        // --- 2. 產出帶有 match_index 的步驟物件 ---
        setPendingStep(
          text
            ? { type: "CLICK_TEXT", text, match_index: matchIndex }
            : { type: "CLICK_BUTTON", selector_type: "LOCATOR", text: label, locator, match_index: matchIndex }
        );
        
        setPendingStepName(label);
        setTempDisplayStepName(label.slice(0, 20));
        
        setStepModalVisible(true);
      };

      iframeWindow.addEventListener("click", handleIframeClick, true);

      if (recordingMachine) {
        switchIframeField(iframeWindow);
      }
    } catch (err) {
      console.warn("無法監聽 iframe 內部事件：", err);
    }
  };

  const handleConfirmStepModal = () => {
    if (!tempDisplayStepName.trim()) {
      message.warning("步驟名稱不可為空");
      return;
    }
    const finalStepName = tempDisplayStepName.trim();
    
    setPathSteps((prev) => [...prev, pendingStep]);
    setDisplayPathSteps((prev) => [...prev, finalStepName]);
    
    message.success(`已新增步驟：${finalStepName}`);
    setStepModalVisible(false);
  };

  const resetImageModal = () => {
    setTempImageUrl("");
    setTempImageFileName("");
    setTempImageCaption("");
    setUploadingImage(false);
  };

  const openImageModal = () => {
    resetImageModal();
    setImageModalVisible(true);
  };

  const closeImageModal = () => {
    setImageModalVisible(false);
    resetImageModal();
  };

  const beforeImageUpload = (file) => {
    const allowed = ["image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type)) {
      message.error("僅支援 PNG、JPG、JPEG、WEBP 圖片");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleImageUploadChange = (info) => {
    if (info.file.status === "uploading") {
      setUploadingImage(true);
      return;
    }

    if (info.file.status === "done") {
      setUploadingImage(false);
      const resData = info.file.response;
      const url = resData?.data?.url || resData?.url || resData?.result?.url || "";
      const filename =
        resData?.data?.filename ||
        resData?.filename ||
        resData?.result?.filename ||
        url.split("/").pop();

      if (!filename) {
        message.error("上傳成功，但伺服器未回傳檔名");
        return;
      }

      setTempImageUrl(`${serverBaseUrl}instruction/image/${filename}`);
      setTempImageFileName(info.file.name);
      message.success(`${info.file.name} 上傳成功`);
    } else if (info.file.status === "error") {
      setUploadingImage(false);
      message.error(`${info.file.name} 上傳失敗`);
    }
  };

  const handleConfirmImageModal = () => {
    if (!tempImageUrl) {
      message.warning("請先上傳圖片");
      return;
    }
    const caption = tempImageCaption.trim() || "說明圖片";

    setPathSteps((prev) => [
      ...prev,
      { type: "IMAGE", image: tempImageUrl, file_name: tempImageFileName, text: caption },
    ]);
    setDisplayPathSteps((prev) => [
      ...prev,
      { type: "IMAGE", image: tempImageUrl, text: caption },
    ]);

    message.success(`已新增圖片步驟：${caption}`);
    closeImageModal();
  };

  const handleStopTracking = () => {
    setIsTracking(false);
    setRecordingModalVisible(false);
    setRecordingMachine(null);
    message.success(`錄製已結束，共記錄 ${pathSteps.length} 個步驟`);
  };

  const addTextSection = () => setSections([...sections, { type: "text", data: "" }]);
  const addFileSection = () => setSections([...sections, { type: "file", data: "", file_name: "" }]);

  const updateSectionData = (index, field, value) => {
    const newSections = [...sections];
    newSections[index][field] = value;
    setSections(newSections);
  };

  const removeSection = async (index, sec) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const moveSection = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= sections.length) return;
    const newSections = [...sections];
    const temp = newSections[index];
    newSections[index] = newSections[targetIndex];
    newSections[targetIndex] = temp;
    setSections(newSections);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      title: record.title,
      target_page_path: record.targetPath,
    });
    setSections(record.sections || []);
    setPathSteps(record.pathSteps || []);
    setDisplayPathSteps(record.displayPathSteps || record.pathSteps || []);
    setDrawerVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        title: values.title,
        target_page_path: values.target_page_path || "",
        path_steps: pathSteps || [],
        display_path_steps: displayPathSteps || [],
        sections: sections || [],
      };

      if (editingId) {
        await UpdateInstruction(editingId, payload);
        message.success("編輯說明成功");
      } else {
        await CreateInstruction(payload);
        message.success("新增說明成功");
      }

      setDrawerVisible(false);
      resetForm();
      fetchInstructions();
    } catch (error) {
      message.error("請檢查輸入資料是否完整");
    }
  };

  const resetForm = () => {
    form.resetFields();
    setEditingId(null);
    setSections([]);
    setPathSteps([]);
    setDisplayPathSteps([]);
    setIsTracking(false);
    setRecordingModalVisible(false);
    setRecordingMachine(null);
    closeImageModal();
  };

  const handleDelete = async (id) => {
    try {
      await DeleteInstruction(id);
      message.success("刪除成功");
      fetchInstructions();
    } catch (error) {
      message.error("刪除失敗");
    }
  };

  const handleJumpToPageInCurrentWindow = (record) => {
    if (!record.targetPath) {
      message.warning("此說明未設定跳轉頁面");
      return;
    }

    const [pathname, search] = record.targetPath.split("?");

    const steps = typeof record.pathSteps === "string"
      ? JSON.parse(record.pathSteps)
      : (record.pathSteps || []);

    history.push({
      pathname: pathname,
      search: search ? `?${search}` : "",
      state: { 
        restoreData: {
          ...record.restoreData,
          instructionId: record.id,
          title: record.title,
          pathSteps: steps,
        },
      },
    });
  };

  const columns = [
    {
      title: "手冊名稱",
      dataIndex: "title",
      key: "title",
      render: (text) => (
        <span>
          <strong>{text}</strong>
        </span>
      ),
    },
    {
      title: "更新日期",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 140,
    },
    {
      title: "操作",
      key: "action",
      width: 260,
      render: (_, record) => (
        <Space size="middle">
          {record.targetPath && (
            <Button
              type="primary"
              icon={<TbHandClick />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleJumpToPageInCurrentWindow(record);
              }}
            >
              展示指引
            </Button>
          )}

          {/* 編輯 / 刪除：解鎖後才顯示 */}
          {unlocked && (
            <>
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                aria-label="編輯"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(record);
                }}
              />

              <Popconfirm
                title="確定要刪除嗎？"
                onConfirm={(e) => {
                  e.stopPropagation();
                  handleDelete(record.id);
                }}
              >
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  aria-label="刪除"
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <React.Fragment>
      <CustomHeader />
      <div className="container-fluid py-4" style={{ minHeight: "calc(100vh - 64px)" }}>
        <div className="container">
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
            <h2 style={{ color: "var(--primary-text)" }}>設定說明手冊</h2>
            <Space>
              {/* 新增說明：解鎖後才顯示 */}
              {unlocked && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    resetForm();
                    setDrawerVisible(true);
                  }}
                >
                  新增說明
                </Button>
              )}

              {/* 鎖頭：鎖定時點擊輸入密碼；解鎖後點擊可重新鎖定 */}
              <Button
                type={unlocked ? "default" : "text"}
                icon={unlocked ? <UnlockOutlined style={{ color: "#52c41a" }} /> : <LockOutlined />}
                aria-label={unlocked ? "重新鎖定" : "解鎖編輯"}
                title={unlocked ? "點擊重新鎖定" : "點擊輸入密碼解鎖"}
                onClick={handleLockClick}
              />
            </Space>
          </div>

          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={data}
              pagination={false}
              expandable={{
                expandIcon: ({ expanded, onExpand, record }) =>
                  expanded ? (
                    <CaretUpFilled onClick={(e) => onExpand(record, e)} style={{ cursor: "pointer", color: "#1890ff" }} />
                  ) : (
                    <CaretRightFilled onClick={(e) => onExpand(record, e)} style={{ cursor: "pointer" }} />
                  ),
                expandedRowRender: (record) => {
                  const stepsToDisplay = record.pathSteps?.length > 0 
                    ? record.pathSteps 
                    : (record.displayPathSteps || []);
                  return (
                    <div style={{ padding: "16px", backgroundColor: "var(--table-background)", borderRadius: "8px" }}>
                      {stepsToDisplay?.length > 0 && (
                        <div style={{ marginBottom: "16px" }}>
                          <span style={{ fontWeight: "bold", marginRight: "8px" }}>操作路徑：</span>
                          <Breadcrumb 
                          style={{ display: "inline-block" }}
                          separator={<span style={{ color: "var(--primary-text)" }}>/</span>}
                          >
                            {stepsToDisplay.map((step, idx) => (
                              <Breadcrumb.Item key={idx}>{renderStepTag(step, "blue")}</Breadcrumb.Item>
                            ))}
                          </Breadcrumb>
                        </div>
                      )}
                      {record.sections?.map((sec, index) => (
                        <div key={index} style={{ marginBottom: "12px" }}>
                          {sec.type === "text" && <p style={{ whiteSpace: "pre-line" }}>{sec.data}</p>}
                          
                          {sec.type === "file" && (
                            <PdfViewer filename={sec.data.split('/').pop()} />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                },
              }}
            />
          </Spin>
        </div>
      </div>

      <Drawer
        title={editingId ? "編輯設定說明" : "新增設定說明"}
        width={640}
        style={{ color: "var(--primary-text)", backgroundColor: "var(--table-background)" }}
        visible={drawerVisible}
        onClose={() => { setDrawerVisible(false); resetForm(); }}
        extra={<Button type="primary" onClick={handleSubmit}>儲存</Button>}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="說明名稱" rules={[{ required: true, message: "請輸入說明名稱" }]}>
            <Input placeholder="例如：A機具新增說明" />
          </Form.Item>

          <Form.Item 
            name="target_page_path" 
            label="類型" 
            rules={[{ required: true, message: "請選擇類型" }]}
          >
            <Radio.Group buttonStyle="solid">
              <Radio.Button 
                value="/control" 
                style={{ 
                  '--ant-wave-shadow-color': '#1890ff',
                  backgroundColor: "var(--button-background)", 
                  color: "var(--primary-text)"
                }}
              >
                <DesktopOutlined style={{ marginRight: 6 }} />
                機具設定
              </Radio.Button>
              <Radio.Button 
                value="/setting"
                style={{ 
                  backgroundColor: "var(--button-background)", 
                  color: "var(--primary-text)"
                }}
              >
                <SettingOutlined style={{ marginRight: 6 }} />
                管理設定
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.target_page_path !== cur.target_page_path}
          >
            {({ getFieldValue }) =>
              getFieldValue("target_page_path") === "/control" ? (
                <Form.Item
                  name="machine_type"
                  label="機具類型"
                  extra="開始錄製時，會自動在教學場域新增此類型的範例機具，並切換到該場域供錄製使用"
                >
                  <Select
                    placeholder="請選擇要錄製的機具類型"
                    options={MACHINE_TYPES}
                    allowClear
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Card 
            title="操作錄製" 
            size="small" 
            style={{ marginBottom: 16 }}
            extra={
              <Space>
                <Button
                  size="small"
                  loading={preparingMachine}
                  onClick={() => handleTrackingChange(true)}
                >
                  開始錄製
                </Button>
                <Button size="small" onClick={() => { setPathSteps([]); setDisplayPathSteps([]); }}>清空</Button>
              </Space>
            }
          >
            <div style={{ minHeight: 32 }}>
              {displayPathSteps.length > 0 ? (
                <Breadcrumb>
                  {displayPathSteps.map((step, idx) => (
                    <Breadcrumb.Item key={idx}>
                      {renderStepTag(step, "green")}
                    </Breadcrumb.Item>
                  ))}
                </Breadcrumb>
              ) : (
                <span style={{ color: "#bfbfbf" }}>{isTracking ? "錄製視窗已開啟..." : "點擊「開啟錄製」將會跳出彈窗嵌入頁面進行記錄"}</span>
              )}
            </div>
          </Card>

          <Card 
            title="說明內文" 
            size="small"
            extra={
              <Space>
                <Button size="small" icon={<PlusOutlined />} onClick={addTextSection}>新增文字</Button>
                <Button size="small" icon={<PlusOutlined />} onClick={addFileSection}>新增文件</Button>
              </Space>
            }
          >
            {sections.map((sec, index) => (
              <Card 
                key={index} 
                type="inner" 
                size="small" 
                title={`區塊 #${index + 1} (${sec.type === "text" ? "文字" : "PDF / 文件檔案"})`}
                style={{ marginBottom: 12}}
                extra={
                  <Space>
                    <Button size="small" type="text" icon={<ArrowUpOutlined />} onClick={() => moveSection(index, -1)} />
                    <Button size="small" type="text" icon={<ArrowDownOutlined />} onClick={() => moveSection(index, 1)} />
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeSection(index)} />
                  </Space>
                }
              >
                {sec.type === "text" ? (
                  <Input.TextArea 
                    rows={3} 
                    placeholder="請輸入說明文字..." 
                    value={sec.data} 
                    onChange={(e) => updateSectionData(index, "data", e.target.value)} 
                  />
                ) : (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Upload
                      name="file"
                      action={uploadApiAction}
                      withCredentials={true}
                      accept=".pdf"
                      showUploadList={false}
                      onChange={(info) => {
                        if (info.file.status === "uploading") {
                          message.loading({ content: "檔案上傳中...", key: `upload-${index}` });
                        }
                        if (info.file.status === "done") {
                          const resData = info.file.response;
                          const fileUrl = resData?.data?.url || resData?.url || resData?.result?.url || "";
                          const newSections = [...sections];
                          newSections[index].data = fileUrl;
                          newSections[index].file_name = info.file.name;
                          setSections(newSections);
                          message.success({ content: `${info.file.name} 上傳成功！`, key: `upload-${index}` });
                        } else if (info.file.status === "error") {
                          message.error({ content: `${info.file.name} 上傳失敗`, key: `upload-${index}` });
                        }
                      }}
                    >
                      <Button icon={<UploadOutlined />}>上傳文件 (PDF)</Button>
                    </Upload>

                    {sec.file_name ? (
                      <div style={{ marginTop: 8, padding: "6px 12px", background: "var(--button-background)", border: "1px solid #dddddd", borderRadius: 4 }}>
                        <FilePdfOutlined style={{ color: "#1890ff", marginRight: 8 }} />
                        <span><strong>{sec.file_name}</strong></span>
                      </div>
                    ) : (
                      <span style={{ color: "#8c8c8c", fontSize: 12 }}>尚未選擇任何檔案</span>
                    )}
                  </Space>
                )}
              </Card>
            ))}
          </Card>
        </Form>
      </Drawer>

      <Modal
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 24 }}>
            <span>
              錄製中 - 請在下方頁面點擊按鈕 (已記錄 {pathSteps.length} 步)
              {recordingMachine && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  範例機具：{recordingMachine.name}
                </Tag>
              )}
            </span>
            <Space>
              <Button icon={<PictureOutlined />} onClick={openImageModal}>
                新增說明圖片
              </Button>
              <Button type="primary" danger onClick={handleStopTracking}>
                結束錄製
              </Button>
            </Space>
          </div>
        }
        visible={recordingModalVisible}
        onCancel={handleStopTracking}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        bodyStyle={{ height: "80vh", padding: 0, position: "relative" }}
        destroyOnClose
      >
        {recordingPath && (
          <iframe
            ref={iframeRef}
            src={recordingPath}
            title="recording-target-page"
            width="100%"
            height="100%"
            style={{ border: "none" }}
            onLoad={handleIframeLoad}
          />
        )}
        {switchingField && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255, 255, 255, 0.6)",
            }}
          >
            <Spin tip="正在切換到教學場域..." />
          </div>
        )}
      </Modal>

      {/* 解鎖密碼視窗 */}
      <Modal
        title={<span><LockOutlined style={{ marginRight: 8 }} />輸入密碼解鎖</span>}
        visible={unlockModalVisible}
        onOk={handleUnlockSubmit}
        onCancel={closeUnlockModal}
        okText="解鎖"
        cancelText="取消"
        width={360}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, fontSize: 13, color: "#666" }}>
          解鎖後可新增、編輯與刪除說明：
        </div>
        <Input.Password
          placeholder="請輸入密碼"
          autoFocus
          value={unlockPassword}
          status={unlockError ? "error" : undefined}
          onChange={(e) => {
            setUnlockPassword(e.target.value);
            if (unlockError) setUnlockError(false);
          }}
          onPressEnter={handleUnlockSubmit}
        />
      </Modal>

      <Modal
        title="確認或自訂步驟名稱"
        visible={stepModalVisible}
        onOk={handleConfirmStepModal}
        onCancel={() => setStepModalVisible(false)}
        okText="確定"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ marginBottom: 12 }}>
          <span style={{ color: "#8c8c8c" }}>偵測到的原始名稱：</span>
          <Tag color="default" style={{ marginLeft: 8 }}>{pendingStepName}</Tag>
        </div>
        <Form layout="vertical">
          <Form.Item label="自訂顯示名稱">
            <Input 
              value={tempDisplayStepName} 
              onChange={(e) => setTempDisplayStepName(e.target.value)}
              onPressEnter={handleConfirmStepModal}
              placeholder="請輸入自訂的步驟名稱..."
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新增說明圖片"
        visible={imageModalVisible}
        onOk={handleConfirmImageModal}
        onCancel={closeImageModal}
        okText="加入步驟"
        cancelText="取消"
        okButtonProps={{ disabled: !tempImageUrl || uploadingImage }}
        zIndex={1100}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Upload
            name="file"
            action={uploadApiAction}
            withCredentials={true}
            accept=".png,.jpg,.jpeg,.webp"
            showUploadList={false}
            beforeUpload={beforeImageUpload}
            onChange={handleImageUploadChange}
          >
            <Button icon={<UploadOutlined />} loading={uploadingImage}>
              {tempImageUrl ? "重新上傳圖片" : "選擇圖片 (PNG / JPG / WEBP)"}
            </Button>
          </Upload>

          {tempImageUrl ? (
            <div style={{ textAlign: "center" }}>
              <img
                src={tempImageUrl}
                alt={tempImageFileName}
                style={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  objectFit: "contain",
                  border: "1px solid #d9d9d9",
                  borderRadius: 6,
                }}
              />
              <div style={{ marginTop: 4, color: "#8c8c8c", fontSize: 12 }}>{tempImageFileName}</div>
            </div>
          ) : (
            <span style={{ color: "#8c8c8c", fontSize: 12 }}>尚未選擇任何圖片</span>
          )}

          <div>
            <div style={{ marginBottom: 4 }}>圖片說明文字（導覽時顯示在圖片上方）</div>
            <Input.TextArea
              rows={2}
              value={tempImageCaption}
              onChange={(e) => setTempImageCaption(e.target.value)}
              placeholder="例如：此為設定完成後的畫面（可留空）"
            />
          </div>
        </Space>
      </Modal>
    </React.Fragment>
  );
}